import React, { Suspense } from 'react'
import cx from 'classnames'
import { Popover, Skeleton } from '@manychat/manyui'

import MCEditorContext from 'components/MCTextEditor/MCEditorContext'
import { IVariableData } from 'components/MCTextEditor/types'
import { SliceComponent } from 'components/RichTextEditor/types'
import { safeReactLazy } from 'utils/safeReactLazy'

import { PopoverCloseReason } from './constants'
import VariableLabel from './VariableLabel'
import VariablePreview from './VariablePreview'

import cm from './Variable.module.css'

const VariableEditor = safeReactLazy(() => import('./VariableEditor'))

const Variable: SliceComponent = ({
  children,
  slice,
  isSelected,
  edit,
  input,
  processor,
  undo,
  redo,
}) => {
  const variableRef = React.useRef(null)
  const { inverse, useContentEditable, variables } = React.useContext(MCEditorContext)

  const handlePopoverCloseReq = (reason: string): void => {
    switch (reason) {
      case PopoverCloseReason.MOVE_TO_START: {
        processor.select(slice.range.start)
        edit(processor.getState())
        input?.focus()
        break
      }
      case PopoverCloseReason.MOVE_TO_END: {
        processor.select(slice.range.end)
        edit(processor.getState())
        input?.focus()
        break
      }
      case PopoverCloseReason.ARROW_KEY: {
        edit(processor.getState())
        input?.focus()
        break
      }
      default: {
        processor.select(slice.range.start)
        edit(processor.getState())
        input?.focus()
      }
    }
  }

  const data = slice.data as IVariableData
  const isInvalid = !variables.getFields().find(({ id }) => id === data.id)

  const handleChange = (variableData: IVariableData) => {
    processor.setSliceData(slice, variableData)
    edit(processor.getState())
  }

  if (variables.config.preview) {
    return <VariablePreview data={data}>{children}</VariablePreview>
  }

  return (
    <React.Fragment>
      <span
        ref={variableRef}
        className={cx(
          cm.variable,
          'rounded',
          isSelected && 'bg-success',
          inverse && cm.inverse,
          !useContentEditable && cm.fixOverlap,
          isInvalid && cm.invalid,
        )}
      >
        <VariableLabel>{children}</VariableLabel>
      </span>
      <Popover
        anchor={variableRef.current}
        at="top"
        open={isSelected}
        onRequestClose={handlePopoverCloseReq}
        className="m-y-xxs"
      >
        <Suspense fallback={<Skeleton.Text lines={6} className="m-a-md" />}>
          <VariableEditor
            variableData={data}
            onChange={handleChange}
            onBlur={handlePopoverCloseReq}
            undo={undo}
            redo={redo}
          />
        </Suspense>
      </Popover>
    </React.Fragment>
  )
}

export default Variable
